import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';
import UserDetailModal from './UserDetailModal';
import { PERM, useCan } from '../permissions';

const manageUserAccount = (payload) => apiPost('/api/admin/manage-user', payload);

const ReportRequest = () => {
    // "Approve & Delete" wipes the account, so it needs the same permission as
    // any other delete. Rejecting a request is ordinary moderation and stays.
    const can = useCan();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    const fetchReports = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            setReports(await apiGet('/api/admin/reports'));
        } catch (err) {
            setError("Failed to load reports. Please check console for details.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchReports(); }, [fetchReports]);

    const handleUpdateRequest = async (reportId, newStatus) => {
        try {
            await apiPost(`/api/admin/reports/${reportId}/status`, { status: newStatus });
            setReports(prev => prev.filter(r => r.id !== reportId));
            alert(`Request has been marked as '${newStatus}'.`);
        } catch (err) { alert("Failed to update the request."); }
    };

    const handleApproveDelete = async (reportId, userIdToDelete) => {
        if (!window.confirm(`Permanently delete user ID: ${userIdToDelete}? This cannot be undone.`)) return;
        try {
            const result = await manageUserAccount({ userId: userIdToDelete, action: 'delete' });
            if (result.success) {
                await apiPost(`/api/admin/reports/${reportId}/status`, { status: 'approved_and_deleted' });
                setReports(prev => prev.filter(r => r.id !== reportId));
                alert(result.message);
            } else { throw new Error(result.message || 'Failed to delete user.'); }
        } catch (err) { alert(`Failed to delete user. Reason: ${err.message}`); }
    };

    const filteredReports = activeTab === 'all'
        ? reports
        : activeTab === 'delete_requests'
            ? reports.filter(r => r.reportType === 'delete_account_request')
            : reports.filter(r => r.reportType !== 'delete_account_request');

    const tabCounts = {
        all: reports.length,
        delete_requests: reports.filter(r => r.reportType === 'delete_account_request').length,
        user_reports: reports.filter(r => r.reportType !== 'delete_account_request').length,
    };

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Reports...</p></div>;
    if (error) return <div className="dark-error"><i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}</div>;

    return (
        <div className="dark-page">
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ margin: 0 }}>User Reports & Requests</h2>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 4 }}>Manage user reports and account deletion requests</p>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 20 }}>
                {[
                    { key: 'all', label: 'All Requests' },
                    { key: 'delete_requests', label: 'Delete Requests' },
                    { key: 'user_reports', label: 'User Reports' },
                ].map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                        padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
                        color: activeTab === tab.key ? '#ff4d6d' : 'rgba(255,255,255,0.5)',
                        borderBottom: activeTab === tab.key ? '2px solid #ff4d6d' : '2px solid transparent',
                        fontWeight: activeTab === tab.key ? 700 : 400,
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8
                    }}>
                        {tab.label}
                        <span style={{ background: activeTab === tab.key ? 'rgba(255,77,109,0.2)' : 'rgba(255,255,255,0.08)', color: activeTab === tab.key ? '#ff4d6d' : 'rgba(255,255,255,0.4)', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                            {tabCounts[tab.key]}
                        </span>
                    </button>
                ))}
            </div>

            {filteredReports.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.3)' }}>
                    <i className="fas fa-file-alt" style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
                    <p style={{ fontSize: 15 }}>No pending {activeTab === 'delete_requests' ? 'delete requests' : activeTab === 'user_reports' ? 'user reports' : 'reports'}.</p>
                </div>
            ) : (
                <div className="dark-table-wrap">
                    <table className="dark-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Reported User</th>
                                <th>Reporter</th>
                                <th>Reason</th>
                                <th>Details</th>
                                <th>Date</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReports.map(report => (
                                <tr key={report.id}>
                                    <td>
                                        <span className={`badge ${report.reportType === 'delete_account_request' ? 'badge-red' : 'badge-blue'}`}>
                                            {report.reportType === 'delete_account_request' ? 'Delete Request' : 'User Report'}
                                        </span>
                                    </td>
                                    <td>
                                        <button onClick={() => setSelectedUser(report.reportedUserId)} className="dark-link">
                                            {report.reportedName}
                                        </button>
                                    </td>
                                    <td>
                                        <button onClick={() => setSelectedUser(report.reporterUserId)} className="dark-link">
                                            {report.reporterName}
                                        </button>
                                    </td>
                                    <td style={{ fontSize: 13 }}>{report.reason}</td>
                                    <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', maxWidth: 160 }} title={report.details}>
                                        {report.details?.substring(0, 30)}...
                                    </td>
                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.5)' }}>
                                        {report.timestamp ? new Date(report.timestamp).toLocaleString() : ''}
                                    </td>
                                    <td className="text-center">
                                        {report.reportType === 'delete_account_request' ? (
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                {can(PERM.DELETE_ACCOUNTS) && (
                                                    <button className="btn-action delete" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => handleApproveDelete(report.id, report.reportedUserId)}>
                                                        Approve & Delete
                                                    </button>
                                                )}
                                                <button className="btn-action warn" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => handleUpdateRequest(report.id, 'rejected')}>
                                                    Reject
                                                </button>
                                            </div>
                                        ) : (
                                            <button className="btn-action" style={{ fontSize: 11, padding: '5px 10px', background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }} onClick={() => handleUpdateRequest(report.id, 'resolved')}>
                                                Resolve
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedUser && <UserDetailModal userId={selectedUser} onClose={() => setSelectedUser(null)} />}
        </div>
    );
};

export default ReportRequest;
